import { useMemo, type ReactNode } from 'react';
import { useDrawHistory } from '@/hooks/useDrawHistory';
import {
  applyAdvancedFilters,
  computeFrequency,
  computeStatsSummary,
  filterByPeriod,
  getColdCombination,
  getColdNumbers,
  getHotCombination,
  getHotNumbers,
  type AdvancedFilters,
} from '@/lib/statisticsAnalysis';
import {
  StatisticsDataContext,
  type StatisticsDataValue,
} from '@/pages/statistics/StatisticsDataContext';

interface StatisticsDataProviderProps {
  periodLimit: number | null;
  advancedFilters: AdvancedFilters;
  onResetFilters: () => void;
  children: ReactNode;
}

/**
 * 통계 페이지 단일 데이터 흐름의 원천.
 * 원본 데이터를 한 번만 로드(useDrawHistory)하고, 데이터 처리 순서를 고정한다:
 * 전체 원본 → 기간 필터 → 고급 필터 → 빈도/hot-cold/조합/요약 파생.
 */
export default function StatisticsDataProvider({
  periodLimit,
  advancedFilters,
  onResetFilters,
  children,
}: StatisticsDataProviderProps) {
  const {
    draws,
    loading,
    error,
    dbCount,
    loadedCount,
    invalidCount,
    incomplete,
    incompleteReason,
    retry,
  } = useDrawHistory();

  const periodFilteredDraws = useMemo(
    () => filterByPeriod(draws, periodLimit),
    [draws, periodLimit],
  );

  const filteredDraws = useMemo(
    () => applyAdvancedFilters(periodFilteredDraws, advancedFilters),
    [periodFilteredDraws, advancedFilters],
  );

  const frequencyData = useMemo(() => computeFrequency(filteredDraws), [filteredDraws]);
  const hotNumbers = useMemo(() => getHotNumbers(frequencyData), [frequencyData]);
  const coldNumbers = useMemo(() => getColdNumbers(frequencyData), [frequencyData]);
  const hotCombination = useMemo(() => getHotCombination(frequencyData), [frequencyData]);
  const coldCombination = useMemo(() => getColdCombination(frequencyData), [frequencyData]);
  const statsSummary = useMemo(
    () => computeStatsSummary(filteredDraws, frequencyData, hotNumbers, coldNumbers),
    [filteredDraws, frequencyData, hotNumbers, coldNumbers],
  );

  const value: StatisticsDataValue = useMemo(
    () => ({
      draws,
      periodFilteredDraws,
      filteredDraws,
      dbCount,
      loadedCount,
      invalidCount,
      incomplete,
      incompleteReason,
      loading,
      error,
      retry,
      frequencyData,
      hotNumbers,
      coldNumbers,
      hotCombination,
      coldCombination,
      statsSummary,
      advancedFilters,
      resetFilters: onResetFilters,
    }),
    [
      draws,
      periodFilteredDraws,
      filteredDraws,
      dbCount,
      loadedCount,
      invalidCount,
      incomplete,
      incompleteReason,
      loading,
      error,
      retry,
      frequencyData,
      hotNumbers,
      coldNumbers,
      hotCombination,
      coldCombination,
      statsSummary,
      advancedFilters,
      onResetFilters,
    ],
  );

  return (
    <StatisticsDataContext.Provider value={value}>
      {children}
    </StatisticsDataContext.Provider>
  );
}